import { startCase } from 'lodash'

/**
 * Attempts to remove all emoji characters from the given string.
 *
 * @param text text to strip emojis from
 * @returns the text with stripped emojis
 */
export function stripEmojis(text: string) {
  // from https://stackoverflow.com/a/41543705

  // Keep in sync with the backend method
  // Make sure it does not crash by checking the function
  if (typeof text?.replaceAll !== 'function') return text

  return text.replaceAll(/([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF])/g, '')
}

/**
 * Attempts to clean up bad formatting in a string containing a person's name.
 * @param name the name to clean
 * @returns the cleaned up name string
 */
export function cleanName(name: string) {
  // Make sure it does not crash by checking the function
  if (typeof name?.trim !== 'function') return name

  return stripEmojis(name.trim().split("'").map(s => startCase(s)).join("'"))
}

/**
 * Returns a string that contains humanized text for a list of names
 * @param names the names to list. Not all the names have to be passed into this array, just the ones that could be explicitly listed
 * @param count the total count of names, potentially including any ones not present in the given list
 * @param limit max number of names to explicitly list. The others will be shown as 'and X others'
 * @returns a string with the names listed with the following format: 'A, B and C' or 'A, B, C and X others' depending on parameters passed
 */
export function nameList(names: string[], count: number, limit = 0) {
  if (!names?.length || !count) return null

  if (names.length === 1) return names[0]

  const length = limit > 0 ? Math.min(limit, names.length) : names.length

  if (names.length > length || count > length) {
    const otherCount = count - length
    return `${names.join(', ')} and ${otherCount} other${otherCount > 1 ? 's' : ''}`
  }

  return `${names.slice(0, length - 1).join(', ')} and ${names[length - 1]}`
}

/**
 * Returns a string that contains humanized text for the list of items provided and the conjuction.
 * @param items the strings to list
 * @param conjunction the conjunction to use such as 'and' or 'or'
 * @returns a humanized string such as 'A, B and C' or 'A, B, C and D' depending on the parameters passed
 */
export function listWithConjunction(items: string[], conjunction: string) {
  if (!items?.length) return null

  if (items.length === 1) return items[0]

  return `${items.slice(0, items.length - 1).join(', ')} ${conjunction} ${items[items.length - 1]}`
}
